import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, LogIn, Mail, KeyRound } from "lucide-react";
import { startGoogleLogin } from "../lib/auth";
import api, { apiErrorMessage } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

/**
 * Sign-in / sign-up dialog. Google Auth stays as the primary path; email/
 * password sits below a subtle divider. Unified identity — if the caller
 * signs up with an email that already has a Google account, the backend
 * attaches the password to the existing user doc rather than creating a
 * duplicate (routers/auth_password.py::signup).
 *
 * View states:
 *   "login"           — email + password, Sign in
 *   "signup"          — email + password + name, Create account
 *   "verify"          — waiting for the 6-digit code
 *   "forgot"          — request a reset code
 *   "reset"           — enter reset code + new password
 */
export default function AuthDialog({ open, onOpenChange, initialView = "login" }) {
  const [view, setView] = useState(initialView);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [tryProTrial, setTryProTrial] = useState(true);  // opt-in by default — friendly nudge
  const [busy, setBusy] = useState(false);

  // Esc closes an untouched dialog; with details typed we keep it open (same
  // guard as a backdrop tap) so a stray key never discards a half-filled form.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (name || email || password || code) {
        toast.info("Tap the ✕ to close — your details are still here.");
      } else {
        setPassword("");
        setCode("");
        setEmail("");
        setName("");
        setView(initialView);
        onOpenChange(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, name, email, password, code]);

  if (!open) return null;

  const reset = () => {
    setPassword("");
    setCode("");
    setBusy(false);
  };

  const close = () => {
    reset();
    setEmail("");
    setName("");
    setView(initialView);
    onOpenChange(false);
  };

  const onSuccessLoggedIn = () => {
    toast.success("Welcome back.");
    close();
    // Reload so the AuthProvider picks up the cookie via /auth/me.
    setTimeout(() => window.location.reload(), 250);
  };

  const err = (e, fallback) => {
    toast.error(apiErrorMessage(e, fallback));
  };

  const doLogin = async () => {
    setBusy(true);
    try {
      await api.post("/auth/login", { email: email.trim(), password });
      onSuccessLoggedIn();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "email_unverified") {
        toast.info("Verify your email first — enter the code you received.");
        setView("verify");
      } else if (!e?.response || e.response.status >= 500) {
        // Gateway blip — one silent retry before we blame the member's password.
        try {
          await new Promise((r) => setTimeout(r, 1500));
          await api.post("/auth/login", { email: email.trim(), password });
          onSuccessLoggedIn();
        } catch (e2) {
          toast.error(apiErrorMessage(e2, "Could not sign in. Try again in a moment."), {
            action: { label: "Retry", onClick: () => doLogin() },
            duration: 10000,
          });
        }
      } else {
        err(e, "Wrong email or password.");
      }
    }
    finally { setBusy(false); }
  };

  const doSignup = async () => {
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    setBusy(true);
    try {
      const r = await api.post("/auth/signup", {
        email: email.trim(),
        password,
        name: name.trim() || undefined,
        try_pro_trial: tryProTrial,
      });
      if (r.data?.verified) {
        // Existing Google account — password attached, already logged in.
        toast.success("Password added to your Google account.");
        onSuccessLoggedIn();
      } else {
        toast.success(
          tryProTrial
            ? `Check ${email.trim()} for the code. Your 7-day Pro trial starts once you verify.`
            : `Check ${email.trim()} for the 6-digit code.`
        );
        setView("verify");
      }
    } catch (e) { err(e, "Could not sign up."); }
    finally { setBusy(false); }
  };

  const doVerify = async () => {
    if (code.length !== 6) return toast.error("Enter the 6-digit code.");
    setBusy(true);
    try {
      await api.post("/auth/verify-email", { email: email.trim(), code });
      onSuccessLoggedIn();
    } catch (e) { err(e, "Invalid or expired code."); }
    finally { setBusy(false); }
  };

  const doForgot = async () => {
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      toast.success(`If ${email.trim()} is registered, a reset code is on the way.`);
      setView("reset");
    } catch (e) { err(e, "Could not send reset code."); }
    finally { setBusy(false); }
  };

  const doReset = async () => {
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (code.length !== 6) return toast.error("Enter the 6-digit code.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email: email.trim(), code, new_password: password });
      onSuccessLoggedIn();
    } catch (e) { err(e, "Invalid or expired code."); }
    finally { setBusy(false); }
  };

  const hasInput = !!(name || email || password || code);

  const onBackdrop = () => {
    if (hasInput) {
      toast.info("Tap the ✕ to close — your details are still here.");
      return;
    }
    close();
  };

  return (
    <div
      /* Scrollable overlay: with the phone keyboard open the viewport can be
         ~400px tall, so the card must scroll rather than centre and clip. */
      className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 py-8 sm:py-4"
      onClick={onBackdrop}
      data-testid="auth-dialog"
    >
      <div
        className="w-full max-w-md my-auto bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg p-6 sm:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute top-1 right-1 h-11 w-11 flex items-center justify-center text-[#888] hover:text-white"
          aria-label="Close"
          data-testid="auth-dialog-close"
        ><X size={18} /></button>

        <p className="label-eyebrow">Boardroom</p>
        <h2 className="font-serif text-3xl mt-1 mb-6">
          {view === "login" && "Sign in"}
          {view === "signup" && "Create your account"}
          {view === "verify" && "Check your inbox"}
          {view === "forgot" && "Reset your password"}
          {view === "reset" && "Set a new password"}
        </h2>

        {/* Google primary — hidden during code entry to reduce clutter */}
        {(view === "login" || view === "signup") && (
          <>
            <Button
              onClick={startGoogleLogin}
              className="w-full h-11 bg-white text-[#0a0a0a] hover:bg-[#f0f0f0] rounded-md font-medium"
              data-testid="auth-google-btn"
            >
              Continue with Google
            </Button>
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-[#2a2a2a]" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#666]">or continue with email</span>
              <div className="flex-1 h-px bg-[#2a2a2a]" />
            </div>
          </>
        )}

        {view === "verify" && (
          <div className="mb-4 p-3 rounded-md border border-[#2a2a2a] bg-[#0a0a0a]" data-testid="auth-verify-help">
            <p className="text-sm text-[#eaeaea] leading-snug">
              We emailed a 6-digit code to{" "}
              <span className="text-[#c89f45] break-all">{email || "your inbox"}</span>.
            </p>
            <p className="text-[11px] text-[#9a9a9a] mt-1.5 leading-snug">
              Enter it below to finish setting up your account
              {tryProTrial ? " and start your 7-day Pro trial" : ""}. It can take a
              minute to arrive — check spam if you don't see it.
            </p>
          </div>
        )}

        <div className="space-y-3">
          {view === "signup" && (
            <Input
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-[#0a0a0a] border-[#2a2a2a] text-[#fafafa] h-11"
              data-testid="auth-name"
            />
          )}

          {(view === "login" || view === "signup" || view === "forgot" || view === "verify" || view === "reset") && (
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#0a0a0a] border-[#2a2a2a] text-[#fafafa] h-11"
              autoFocus={view !== "verify" && view !== "reset"}
              data-testid="auth-email"
              readOnly={view === "verify" || view === "reset"}
            />
          )}

          {(view === "verify" || view === "reset") && (
            <Input
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="bg-[#0a0a0a] border-[#2a2a2a] text-[#c89f45] h-11 font-mono text-lg tracking-[0.4em] text-center"
              autoFocus
              data-testid="auth-code"
            />
          )}

          {(view === "login" || view === "signup" || view === "reset") && (
            <Input
              type="password"
              placeholder={view === "reset" ? "New password (8+ chars)" : "Password (8+ chars)"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#0a0a0a] border-[#2a2a2a] text-[#fafafa] h-11"
              data-testid="auth-password"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (view === "login") doLogin();
                  else if (view === "signup") doSignup();
                  else if (view === "reset") doReset();
                }
              }}
            />
          )}

          {/* Pro-trial opt-in — signup only. Checked by default so we can
              measure lift; user can uncheck to just take the free tier. */}
          {view === "signup" && (
            <label
              htmlFor="pro-trial-optin"
              className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                tryProTrial
                  ? "border-[#c89f45]/60 bg-[#c89f45]/[0.06]"
                  : "border-[#2a2a2a] hover:border-[#3a3a3a]"
              }`}
              data-testid="auth-trial-optin"
            >
              <input
                id="pro-trial-optin"
                type="checkbox"
                checked={tryProTrial}
                onChange={(e) => setTryProTrial(e.target.checked)}
                className="mt-0.5 h-5 w-5 accent-[#c89f45] shrink-0"
                data-testid="auth-trial-checkbox"
              />
              <div className="min-w-0">
                <p className="text-sm text-[#fafafa] leading-tight">
                  Start 7-day <span className="text-[#c89f45] font-medium">Pro trial</span>
                </p>
                <p className="text-[11px] text-[#888] mt-1 leading-snug">
                  Unlimited AI minutes, live partner sessions, transcription. No card
                  required — auto-downgrades to Free after 7 days.
                </p>
              </div>
            </label>
          )}

          <Button
            onClick={
              view === "login" ? doLogin
              : view === "signup" ? doSignup
              : view === "verify" ? doVerify
              : view === "forgot" ? doForgot
              : doReset
            }
            disabled={busy || !email.trim() || (view === "login" && !password) || (view === "signup" && !password)}
            className="w-full h-11 bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md font-medium disabled:opacity-40"
            data-testid="auth-submit"
          >
            {busy ? "Working…" : (
              view === "login" ? "Sign in" :
              view === "signup" ? "Create account" :
              view === "verify" ? "Verify & sign in" :
              view === "forgot" ? "Send reset code" :
              "Set new password"
            )}
          </Button>
        </div>

        {/* Footer links — mode-switching */}
        <div className="mt-5 text-center text-xs text-[#9a9a9a] space-y-1 [&_button]:min-h-[40px] [&_button]:px-2 [&_button]:py-2">
          {view === "login" && (
            <>
              <p>
                <button onClick={() => { reset(); setView("forgot"); }} className="text-[#c89f45] hover:underline" data-testid="auth-switch-forgot">
                  Forgot your password?
                </button>
              </p>
              <p>
                New here?{" "}
                <button onClick={() => { reset(); setView("signup"); }} className="text-[#c89f45] hover:underline" data-testid="auth-switch-signup">
                  Create an account
                </button>
              </p>
            </>
          )}
          {view === "signup" && (
            <p>
              Already have an account?{" "}
              <button onClick={() => { reset(); setView("login"); }} className="text-[#c89f45] hover:underline" data-testid="auth-switch-login">
                Sign in
              </button>
            </p>
          )}
          {(view === "verify" || view === "reset") && (
            <p>
              Wrong email?{" "}
              <button onClick={() => { reset(); setView(view === "verify" ? "signup" : "forgot"); }} className="text-[#c89f45] hover:underline">
                Start over
              </button>
            </p>
          )}
          {view === "forgot" && (
            <p>
              <button onClick={() => { reset(); setView("login"); }} className="text-[#c89f45] hover:underline">
                Back to sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
